import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import siteConfig from "site-config";
import { normalizeData, getRecordsFromSchema } from "lib/api";
import { stringify } from "querystring";
import {
    FEATURED_CATALOGS_FETCH,
    FEATURED_CATALOGS_FETCH_CANCEL,
    FEATURED_CATALOGS_COMPLETED,
    SCOPE_VIEW_AS,
    CATALOGS,
    CATALOG,
} from "constants/action-types";
import CatalogsSchema from "schemas/catalogs";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { updateEntities } from "redux-query";
import { defaultCheckError, progressEpic } from "epics/base-epic";
import { IImportCatalogPayload, IImportCatalogRequest, IImportCatalogResponse } from "reducers/catalog-import-reducer";

export const fetchFeaturedCatalogsEpic = (action$, store) =>
    Observable.combineLatest<{ payload: any }, {}>(
        action$.ofType(FEATURED_CATALOGS_FETCH),
        action$.ofType(SCOPE_VIEW_AS).startWith({}),
    )
        .mergeMap(([{ payload }]) => {
            const { active, pageSize, type } = payload;

            const { scope } = store.getState();
            const { viewAs, brand } = scope;

            if (!brand || !brand._id) {
                throw new Error("No brand set");
            }

            const query = {
                __limit: pageSize,
                __meta: true,
                __company: viewAs && viewAs._id,
                type,
            };

            if (active !== undefined) {
                (query as any).active = active;
            }

            return ajax(`${siteConfig.api}/api/v3.1/catalogs?${stringify(query)}`)
                .takeUntil(action$.ofType(FEATURED_CATALOGS_FETCH_CANCEL))
                .mergeMap(({ response, status, xhr }) => {
                    const data = normalizeData(response, CatalogsSchema);
                    return [
                        {
                            payload: data,
                            type: FEATURED_CATALOGS_COMPLETED,
                        },
                    ];
                })
                .catch((error) => [
                    {
                        error: true,
                        payload: error,
                        type: FEATURED_CATALOGS_COMPLETED,
                    },
                    snackbarAdd({ message: formatMessage("Catalog unavailable") }),
                ]);
        })
        .catch((error) => {
            return Observable.merge(
                Observable.of({
                    error: true,
                    payload: error,
                    type: FEATURED_CATALOGS_COMPLETED,
                }),
            );
        });

export const fetchCatalogsEpic = (action$, store) => {
    return action$.ofType(CATALOGS.FETCH.START).flatMap(({ payload, meta }) => {
        let endpoint;
        if (meta && meta.nextPage) {
            endpoint = meta.nextPage;
        } else {
            const { active, pageSize, type, sort, sortOrder, search } = payload;
            const { viewAs } = store.getState().scope;

            const query = {
                __limit: pageSize,
                __meta: true,
                __company: viewAs && viewAs._id,
                __sort: sort,
                __sort_order: sortOrder,
                __populate: "__entries",
                type,
                ...(search && { __search: search }),
            };

            if (active !== undefined) {
                (query as any).active = active;
            }

            endpoint = `/api/v3.1/catalogs?${stringify(query)}`;
        }

        return ajax(`${siteConfig.api}${endpoint}`)
            .map(({ xhr, response }) => {
                const nextPage = xhr.getResponseHeader("Next");
                const records = getRecordsFromSchema(CatalogsSchema);
                const data = normalizeData(response, CatalogsSchema, records);
                return { entities: data.entities, result: data.result, nextPage };
            })
            .mergeMap(({ entities, result, nextPage }: any) => {
                return [
                    updateEntities({
                        catalogs: (catalogs = {}) => {
                            return {
                                ...catalogs,
                                ...entities.catalogs,
                            };
                        },
                    }),
                    {
                        meta: {
                            ...meta,
                            replace: !meta.nextPage,
                        },
                        payload: {
                            result,
                            nextPage,
                        },
                        type: CATALOGS.FETCH.COMPLETED,
                    },
                ];
            })
            .catch((error) => [
                {
                    error: true,
                    payload: error,
                    type: CATALOGS.FETCH.COMPLETED,
                },
                snackbarAdd({ message: formatMessage("Cannot retrieve catalogs") }),
            ]);
    });
};

export const catalogImportEpic = progressEpic<IImportCatalogRequest, IImportCatalogPayload>(
    CATALOG.IMPORT,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            module: "default",
        },
    }),
    {
        dataCustomizer: (response: IImportCatalogResponse) => ({
            errors: response.errors,
            message: response.localizedMessage,
        }),
        checkErrorStatus: defaultCheckError,
    },
);
